/**
 * RecurrenceInfo — encodes the `<parent>_<instanceTimestamp>` id
 * convention used to identify recurring-event masters and their
 * client-synthesized instances.
 *
 * A `Parent` id is the base event id (e.g. `evt_abc`). An
 * `Instance` id concatenates the parent's id + the instance's
 * start timestamp in compact form (`evt_abc_20260418T150000Z`).
 *
 * `RecurrenceInfo.make(rawId)` parses either form:
 *   - no underscore     → `Parent`
 *   - `<id>_<YYYYMMDD>` → `Instance` (full-day)
 *   - `<id>_<YYYYMMDDTHHMMSSZ>` → `Instance` (partial-day, UTC)
 *   - anything else     → falls back to `Parent` with a warning.
 *
 * Port of lona-js's `recurrence-info.ts`. Malformed ids are reported
 * through `Tracing.log` breadcrumbs (`recurrence-info/*`).
 */

import { EventId } from "../../ids/ids.ts";
import type { Option } from "../../core/option.ts";
import { DateTime, NaiveDate, Utc } from "@tento-chrono";
import { Tracing } from "@tento-core/tracing";
import { nonnull } from "@tento-core/assert";
import type { CalendarEvent } from "../../core/dtype.ts";

/**
 * What recurrence an event carries: the master of a series or one instance of it, which the event
 * renderers and editors read.
 */
export type RecurrenceInfo = RecurrenceInfo.Parent | RecurrenceInfo.Instance;

/** Values that go with `RecurrenceInfo`: `Parent`, `Instance`, `ofEvent`, `make`. */
export namespace RecurrenceInfo {
  export class Parent {
    constructor(readonly id: EventId) {
      if (this.id.indexOf("_") !== -1) {
        Tracing.log("recurrence-info/invalid-parent-id", { id });
      }
    }

    get parentId(): EventId {
      return this.id;
    }
  }

  export class Instance {
    constructor(
      readonly parentId: EventId,
      readonly date: NaiveDate | DateTime<Utc>,
    ) {
      if (this.parentId.indexOf("_") !== -1) {
        Tracing.log("recurrence-info/invalid-instance-parent-id", {
          parentId,
        });
      }
    }

    // Both fields are readonly, so the id is formatted once: expansion reads
    // it several times per occurrence (index key, synthetic event, item id).
    #id: EventId | undefined;

    /** `<parentId>_<compact date>`: derived from a non-empty parent id, so never empty. */
    get id(): EventId {
      return this.#id ??= EventId.of(`${this.parentId}_${
        this.date.toString().replaceAll("-", "").replaceAll(":", "")
      }`);
    }
  }

  /**
   * The recurrence info an event's own fields imply: `make(event.id)` when the
   * event recurs (it carries a `recurrence`), `null` when it does not.
   */
  export function ofEvent(event: Pick<CalendarEvent, "id" | "recurrence">): Option<RecurrenceInfo> {
    return event.recurrence != null ? make(event.id) : null;
  }

  export function make(rawId: EventId): RecurrenceInfo {
    const underscoreIndex = rawId.indexOf("_");
    // An id with no `_`, or one that starts with it, names a parent.
    const parentId = underscoreIndex === -1 ? null : EventId.parse(rawId.substring(0, underscoreIndex));
    if (parentId == null) {
      return new RecurrenceInfo.Parent(rawId);
    }

    const dt = rawId.substring(underscoreIndex + 1);

    if (dt.includes("T")) {
      // Handle potential 'R' prefix for recurring events
      const cleanDt = dt.startsWith("R") ? dt.substring(1) : dt;

      // Convert compact format to RFC3339/ISO format by adding separators
      // 20250828T050000Z -> 2025-08-28T05:00:00Z
      const rfc3339String = cleanDt.replace(
        /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})(.*)$/,
        "$1-$2-$3T$4:$5:$6$7",
      );

      const dateTimeResult = DateTime.fromRfc3339(rfc3339String);
      if (dateTimeResult.asOk()) {
        const utcDateTime = nonnull(dateTimeResult.asOk(), "recurrence-info: exdate parsed").toUtc();
        return new RecurrenceInfo.Instance(parentId, utcDateTime);
      }
    } else if (/^\d{8}$/.test(dt)) {
      // NaiveDate format: 20250827 -> 2025-08-27
      const isoDate = dt.replace(/^(\d{4})(\d{2})(\d{2})$/, "$1-$2-$3");
      const naiveDate = NaiveDate.parse(isoDate);
      if (naiveDate) {
        return new RecurrenceInfo.Instance(parentId, naiveDate);
      }
    }

    // Fallback: treat as Parent if we can't parse the suffix
    Tracing.log("recurrence-info/unparsed-instance-id", { rawId });
    return new RecurrenceInfo.Parent(parentId);
  }
}
